/**
 * useDataWriteback Hook
 *
 * Provides `updateRecord(recordId, fieldChanges)` for PM components
 * (Kanban, Gantt, Calendar) that need to write data back to the data source.
 *
 * Implements optimistic update + rollback pattern:
 *   1. Caller applies optimistic local state change
 *   2. Hook fires PUT to data-service
 *   3. On failure, caller rolls back via onRollback callback
 *
 * Follows useQueryTemplateData.ts patterns for auth, error handling, and refresh.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { WritebackConfig } from '@/types/pm-types';
import {
  parseComponentDataBindingWriteTarget,
  type ComponentDataBindingWriteTarget,
  type ComponentDataWriteOperation,
  type DataSourceRef,
  type FederatedViewSourceWriteTarget,
} from '@/types/componentDataBinding';
import dataApi from '@/services/api/dataApi';
import {
  mutateComponentDataViewSource,
  mutateComponentSchemaSource,
} from '@/services/api/componentSchemaBindingApi';
import { getRefetch } from '@/stores/data/refresh-store';

type DataWritebackTargetMode =
  | 'explicit-schema'
  | 'explicit-federated-view-source'
  | 'legacy-same-context';
type DataWritebackTargetStatus =
  | 'ready'
  | 'disabled'
  | 'select-target'
  | 'manual-review-required'
  | 'source-write-authority-required';
type DataWritebackReconnectCode =
  | 'select-target'
  | 'manual-review-required'
  | 'source-write-authority-required';
type DataWritebackOperationPermissions = Partial<
  Record<ComponentDataWriteOperation, boolean>
>;

interface DataWritebackTarget {
  mode: DataWritebackTargetMode;
  source: DataSourceRef | null;
  federatedWriteTarget: FederatedViewSourceWriteTarget | null;
  requestingOrganizationId: string | null;
  dataOwnerOrganizationId: string | null;
  groupId: string;
  schemaId: string;
}

export type DataWritebackTargetResolution =
  | {
      status: 'ready';
      operation: ComponentDataWriteOperation;
      target: DataWritebackTarget;
      reconnect: null;
      message: null;
    }
  | {
      status: Exclude<DataWritebackTargetStatus, 'ready'>;
      operation: ComponentDataWriteOperation;
      target: null;
      reconnect: {
        code: DataWritebackReconnectCode;
        message: string;
      } | null;
      message: string;
    };

interface UseDataWritebackOptions {
  /** Writeback configuration from component properties */
  writebackConfig: WritebackConfig | undefined;
  /** Explicit Phase 5 write target from ComponentDataBinding.writeTarget */
  writeTarget?: unknown;
  /** Blocks legacy same-context writes when a component binding owns writes */
  requireExplicitWriteTarget?: boolean;
  /** Operation-specific capabilities from the source resolver, when available */
  writePermissions?: DataWritebackOperationPermissions;
  /** Requesting org context returned by explicit write-target readiness resolution */
  writeTargetRequestingOrganizationId?: string | null;
  /** Component ID for refresh-store integration */
  componentId: string;
}

interface UseDataWritebackResult {
  /** Create a record in the resolved write target. Returns true on success, false on failure. */
  createRecord: (
    recordData: Record<string, unknown>,
    options?: {
      onOptimisticUpdate?: () => void;
      onRollback?: () => void;
      metadata?: { source?: string; tags?: string[] };
    }
  ) => Promise<boolean>;
  /** Update a single record's fields. Returns true on success, false on failure. */
  updateRecord: (
    recordId: string,
    fieldChanges: Record<string, unknown>,
    options?: {
      onOptimisticUpdate?: () => void;
      onRollback?: () => void;
    }
  ) => Promise<boolean>;
  /** Delete a record in the resolved write target. */
  deleteRecord: (
    recordId: string,
    options?: {
      onOptimisticUpdate?: () => void;
      onRollback?: () => void;
    }
  ) => Promise<boolean>;
  /** Whether a write is currently in-flight */
  isUpdating: boolean;
  /** Last error message, cleared on next successful write */
  error: string | null;
  /** Current write-target readiness for reconnect/select-target UI */
  writeTargetResolution: DataWritebackTargetResolution;
  /** Resolve readiness for a specific mutation operation. */
  resolveWriteTarget: (
    operation: ComponentDataWriteOperation,
  ) => DataWritebackTargetResolution;
}

function getTrimmedString(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function getOptionalTrimmedString(
  value: string | null | undefined,
): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  return getTrimmedString(value);
}

function getLegacyTarget(
  writebackConfig: WritebackConfig | undefined,
): DataWritebackTarget | null {
  if (!writebackConfig?.enabled) {
    return null;
  }

  const groupId = getTrimmedString(writebackConfig.groupId);
  const schemaId = getTrimmedString(writebackConfig.schemaId);
  if (groupId === null || schemaId === null) {
    return null;
  }

  return {
    mode: 'legacy-same-context',
    source: null,
    federatedWriteTarget: null,
    requestingOrganizationId: null,
    dataOwnerOrganizationId: getOptionalTrimmedString(
      writebackConfig.dataOwnerOrganizationId,
    ),
    groupId,
    schemaId,
  };
}

function getExplicitTarget(
  writeTarget: ComponentDataBindingWriteTarget,
  requestingOrganizationId: string | null | undefined,
): DataWritebackTarget {
  const source =
    writeTarget.kind === 'federated-view-source'
      ? writeTarget.source
      : writeTarget;

  return {
    mode:
      writeTarget.kind === 'federated-view-source'
        ? 'explicit-federated-view-source'
        : 'explicit-schema',
    source,
    federatedWriteTarget:
      writeTarget.kind === 'federated-view-source' ? writeTarget : null,
    requestingOrganizationId: getOptionalTrimmedString(requestingOrganizationId),
    dataOwnerOrganizationId: source.dataOwnerOrganizationId.trim(),
    groupId: source.groupId.trim(),
    schemaId: source.schemaId.trim(),
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

async function mutateExplicitTarget(
  target: DataWritebackTarget,
  operation: ComponentDataWriteOperation,
  payload: {
    recordId?: string;
    data: Record<string, unknown>;
    metadata?: { source?: string; tags?: string[] };
  },
): Promise<void> {
  if (target.requestingOrganizationId === null) {
    throw new Error(
      'Resolved requesting organization is required for explicit write target mutations.',
    );
  }

  if (target.mode === 'explicit-federated-view-source') {
    if (target.federatedWriteTarget === null) {
      throw new Error(
        'Resolved federated write target is required for DataViewRef source mutations.',
      );
    }
    if (operation === 'create') {
      await mutateComponentDataViewSource({
        writeTarget: target.federatedWriteTarget,
        requestingOrganizationId: target.requestingOrganizationId,
        operation,
        data: payload.data,
        metadata: payload.metadata,
      });
      return;
    }
    if (payload.recordId === undefined) {
      throw new Error('recordId is required for explicit write target mutations.');
    }
    if (operation === 'update') {
      await mutateComponentDataViewSource({
        writeTarget: target.federatedWriteTarget,
        requestingOrganizationId: target.requestingOrganizationId,
        operation,
        recordId: payload.recordId,
        data: payload.data,
        metadata: payload.metadata,
      });
      return;
    }
    await mutateComponentDataViewSource({
      writeTarget: target.federatedWriteTarget,
      requestingOrganizationId: target.requestingOrganizationId,
      operation,
      recordId: payload.recordId,
    });
    return;
  }

  if (target.source === null) {
    throw new Error('Resolved source is required for explicit write target mutations.');
  }
  if (operation === 'create') {
    await mutateComponentSchemaSource({
      source: target.source,
      requestingOrganizationId: target.requestingOrganizationId,
      operation,
      data: payload.data,
      metadata: payload.metadata,
    });
    return;
  }
  if (payload.recordId === undefined) {
    throw new Error('recordId is required for explicit write target mutations.');
  }
  if (operation === 'update') {
    await mutateComponentSchemaSource({
      source: target.source,
      requestingOrganizationId: target.requestingOrganizationId,
      operation,
      recordId: payload.recordId,
      data: payload.data,
      metadata: payload.metadata,
    });
    return;
  }
  await mutateComponentSchemaSource({
    source: target.source,
    requestingOrganizationId: target.requestingOrganizationId,
    operation,
    recordId: payload.recordId,
  });
}

export function resolveDataWritebackTarget(options: {
  writeTarget?: unknown;
  writebackConfig?: WritebackConfig;
  requireExplicitWriteTarget?: boolean;
  writePermissions?: DataWritebackOperationPermissions;
  writeTargetRequestingOrganizationId?: string | null;
  operation?: ComponentDataWriteOperation;
}): DataWritebackTargetResolution {
  const operation = options.operation ?? 'update';
  const parsedWriteTarget =
    options.writeTarget === undefined
      ? undefined
      : parseComponentDataBindingWriteTarget(options.writeTarget);

  if (options.writeTarget !== undefined && parsedWriteTarget === undefined) {
    return {
      status: 'manual-review-required',
      operation,
      target: null,
      reconnect: {
        code: 'manual-review-required',
        message:
          'Write target is malformed and requires manual review before writing.',
      },
      message:
        'Write target is malformed and requires manual review before writing.',
    };
  }

  if (
    options.requireExplicitWriteTarget === true &&
    parsedWriteTarget === undefined
  ) {
    return {
      status: 'select-target',
      operation,
      target: null,
      reconnect: {
        code: 'select-target',
        message:
          'Select a write target before creating, updating, or deleting records.',
      },
      message:
        'Select a write target before creating, updating, or deleting records.',
    };
  }

  const target =
    parsedWriteTarget !== undefined
      ? getExplicitTarget(
          parsedWriteTarget,
          options.writeTargetRequestingOrganizationId,
        )
      : getLegacyTarget(options.writebackConfig);

  if (target === null) {
    return {
      status: 'disabled',
      operation,
      target: null,
      reconnect: null,
      message: 'Writeback is not enabled.',
    };
  }

  const operationAllowed = options.writePermissions?.[operation];
  if (parsedWriteTarget !== undefined && operationAllowed === false) {
    return {
      status: 'source-write-authority-required',
      operation,
      target: null,
      reconnect: {
        code: 'source-write-authority-required',
        message: `${operation} permission is required for the selected target.`,
      },
      message: `${operation} permission is required for the selected target.`,
    };
  }

  if (
    options.requireExplicitWriteTarget === true &&
    parsedWriteTarget !== undefined &&
    operationAllowed !== true
  ) {
    return {
      status: 'source-write-authority-required',
      operation,
      target: null,
      reconnect: {
        code: 'source-write-authority-required',
        message: `Resolved ${operation} permission is required for the selected target.`,
      },
      message: `Resolved ${operation} permission is required for the selected target.`,
    };
  }

  if (
    options.requireExplicitWriteTarget === true &&
    parsedWriteTarget !== undefined &&
    getOptionalTrimmedString(options.writeTargetRequestingOrganizationId) ===
      null
  ) {
    return {
      status: 'source-write-authority-required',
      operation,
      target: null,
      reconnect: {
        code: 'source-write-authority-required',
        message:
          'Resolved requesting organization is required for the selected target.',
      },
      message:
        'Resolved requesting organization is required for the selected target.',
    };
  }

  return {
    status: 'ready',
    operation,
    target,
    reconnect: null,
    message: null,
  };
}

export function useDataWriteback({
  writebackConfig,
  writeTarget,
  requireExplicitWriteTarget = false,
  writePermissions,
  writeTargetRequestingOrganizationId,
  componentId,
}: UseDataWritebackOptions): UseDataWritebackResult {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const writeTargetResolution = useMemo(
    () =>
      resolveDataWritebackTarget({
        writeTarget,
        writebackConfig,
        requireExplicitWriteTarget,
        writePermissions,
        writeTargetRequestingOrganizationId,
        operation: 'update',
      }),
    [
      requireExplicitWriteTarget,
      writePermissions,
      writeTarget,
      writeTargetRequestingOrganizationId,
      writebackConfig,
    ],
  );

  const resolveWriteTarget = useCallback(
    (operation: ComponentDataWriteOperation) =>
      resolveDataWritebackTarget({
        writeTarget,
        writebackConfig,
        requireExplicitWriteTarget,
        writePermissions,
        writeTargetRequestingOrganizationId,
        operation,
      }),
    [
      requireExplicitWriteTarget,
      writePermissions,
      writeTarget,
      writeTargetRequestingOrganizationId,
      writebackConfig,
    ],
  );

  const validateMutationIds = useCallback(
    (
      resolution: DataWritebackTargetResolution,
      recordId?: string,
    ): boolean => {
      if (resolution.status !== 'ready') {
        return false;
      }

      const { dataOwnerOrganizationId, groupId, schemaId } = resolution.target;
      const objectIdRegex = /^[0-9a-fA-F]{24}$/;
      const hasInvalidExplicitOwner =
        dataOwnerOrganizationId !== null &&
        !objectIdRegex.test(dataOwnerOrganizationId);
      const hasInvalidRecordId =
        recordId !== undefined && !objectIdRegex.test(recordId);

      if (
        hasInvalidExplicitOwner ||
        !objectIdRegex.test(groupId) ||
        !objectIdRegex.test(schemaId) ||
        hasInvalidRecordId
      ) {
        setError(
          recordId === undefined
            ? 'Invalid ID format — dataOwnerOrganizationId, groupId, or schemaId is not a valid ObjectId'
            : 'Invalid ID format — dataOwnerOrganizationId, groupId, schemaId, or recordId is not a valid ObjectId',
        );
        return false;
      }

      return true;
    },
    [],
  );

  const refreshAfterMutation = useCallback(() => {
    const refetch = getRefetch(componentId);
    if (refetch) {
      refetch().catch((error: unknown) => {
        console.warn('[useDataWriteback] Post-writeback refresh failed', {
          componentId,
          error: getErrorMessage(error, 'Refresh failed'),
        });
      });
    }
  }, [componentId]);

  const createRecord = useCallback(
    async (
      recordData: Record<string, unknown>,
      options?: {
        onOptimisticUpdate?: () => void;
        onRollback?: () => void;
        metadata?: { source?: string; tags?: string[] };
      }
    ): Promise<boolean> => {
      const createResolution = resolveWriteTarget('create');

      if (createResolution.status !== 'ready') {
        setError(createResolution.message);
        console.warn('[useDataWriteback] Write target is not ready', {
          componentId,
          operation: createResolution.operation,
          status: createResolution.status,
        });
        return false;
      }

      if (isUpdatingRef.current) {
        console.debug('[useDataWriteback] Skipped (already updating)', { componentId });
        return false;
      }

      if (!validateMutationIds(createResolution)) {
        return false;
      }

      const { dataOwnerOrganizationId, groupId, schemaId, mode } =
        createResolution.target;

      isUpdatingRef.current = true;
      if (mountedRef.current) {
        setIsUpdating(true);
        setError(null);
      }

      options?.onOptimisticUpdate?.();

      try {
        if (mode !== 'legacy-same-context') {
          await mutateExplicitTarget(createResolution.target, 'create', {
            data: recordData,
            metadata: options?.metadata ?? { source: 'kanban', tags: ['kanban'] },
          });
        } else {
          await dataApi.createRecord(
            groupId,
            schemaId,
            recordData,
            options?.metadata ?? { source: 'kanban', tags: ['kanban'] },
            dataOwnerOrganizationId === null
              ? undefined
              : { dataOwnerOrganizationId },
          );
        }

        if (!mountedRef.current) return true;

        console.debug('[useDataWriteback] Record created', {
          componentId,
          fields: Object.keys(recordData),
        });

        refreshAfterMutation();

        return true;
      } catch (err) {
        if (!mountedRef.current) return false;

        const errorMessage = getErrorMessage(err, 'Failed to create record');
        setError(errorMessage);
        options?.onRollback?.();

        console.error('[useDataWriteback] Create failed', {
          componentId,
          error: errorMessage,
        });

        return false;
      } finally {
        isUpdatingRef.current = false;
        if (mountedRef.current) {
          setIsUpdating(false);
        }
      }
    },
    [componentId, refreshAfterMutation, resolveWriteTarget, validateMutationIds],
  );

  const updateRecord = useCallback(
    async (
      recordId: string,
      fieldChanges: Record<string, unknown>,
      options?: {
        onOptimisticUpdate?: () => void;
        onRollback?: () => void;
      }
    ): Promise<boolean> => {
      if (writeTargetResolution.status !== 'ready') {
        setError(writeTargetResolution.message);
        console.warn('[useDataWriteback] Write target is not ready', {
          componentId,
          status: writeTargetResolution.status,
        });
        return false;
      }

      if (isUpdatingRef.current) {
        console.debug('[useDataWriteback] Skipped (already updating)', { componentId });
        return false;
      }

      const { dataOwnerOrganizationId, groupId, schemaId, mode } =
        writeTargetResolution.target;

      if (!validateMutationIds(writeTargetResolution, recordId)) {
        return false;
      }

      isUpdatingRef.current = true;
      if (mountedRef.current) {
        setIsUpdating(true);
        setError(null);
      }

      // Apply optimistic update
      options?.onOptimisticUpdate?.();

      try {
        if (mode !== 'legacy-same-context') {
          await mutateExplicitTarget(writeTargetResolution.target, 'update', {
            recordId,
            data: fieldChanges,
          });
        } else {
          await dataApi.updateRecord(
            groupId,
            schemaId,
            recordId,
            fieldChanges,
            dataOwnerOrganizationId === null
              ? undefined
              : { dataOwnerOrganizationId },
          );
        }

        if (!mountedRef.current) return true;

        console.debug('[useDataWriteback] Record updated', {
          componentId,
          recordId,
          fields: Object.keys(fieldChanges),
        });

        refreshAfterMutation();

        return true;
      } catch (err) {
        if (!mountedRef.current) return false;

        const errorMessage = getErrorMessage(err, 'Failed to update record');
        setError(errorMessage);

        // Rollback optimistic update
        options?.onRollback?.();

        console.error('[useDataWriteback] Update failed', {
          componentId,
          recordId,
          error: errorMessage,
        });

        return false;
      } finally {
        isUpdatingRef.current = false;
        if (mountedRef.current) {
          setIsUpdating(false);
        }
      }
    },
    [writeTargetResolution, componentId, refreshAfterMutation, validateMutationIds]
  );

  const deleteRecord = useCallback(
    async (
      recordId: string,
      options?: {
        onOptimisticUpdate?: () => void;
        onRollback?: () => void;
      },
    ): Promise<boolean> => {
      const deleteResolution = resolveWriteTarget('delete');

      if (deleteResolution.status !== 'ready') {
        setError(deleteResolution.message);
        console.warn('[useDataWriteback] Write target is not ready', {
          componentId,
          operation: deleteResolution.operation,
          status: deleteResolution.status,
        });
        return false;
      }

      if (isUpdatingRef.current) {
        console.debug('[useDataWriteback] Skipped (already updating)', { componentId });
        return false;
      }

      if (!validateMutationIds(deleteResolution, recordId)) {
        return false;
      }

      const { dataOwnerOrganizationId, groupId, schemaId, mode } =
        deleteResolution.target;

      isUpdatingRef.current = true;
      if (mountedRef.current) {
        setIsUpdating(true);
        setError(null);
      }

      options?.onOptimisticUpdate?.();

      try {
        if (mode !== 'legacy-same-context') {
          await mutateExplicitTarget(deleteResolution.target, 'delete', {
            recordId,
            data: {},
          });
        } else {
          await dataApi.deleteRecord(
            groupId,
            schemaId,
            recordId,
            dataOwnerOrganizationId === null
              ? undefined
              : { dataOwnerOrganizationId },
          );
        }

        if (!mountedRef.current) return true;

        console.debug('[useDataWriteback] Record deleted', {
          componentId,
          recordId,
        });

        refreshAfterMutation();

        return true;
      } catch (err) {
        if (!mountedRef.current) return false;

        const errorMessage = getErrorMessage(err, 'Failed to delete record');
        setError(errorMessage);
        options?.onRollback?.();

        console.error('[useDataWriteback] Delete failed', {
          componentId,
          recordId,
          error: errorMessage,
        });

        return false;
      } finally {
        isUpdatingRef.current = false;
        if (mountedRef.current) {
          setIsUpdating(false);
        }
      }
    },
    [componentId, refreshAfterMutation, resolveWriteTarget, validateMutationIds],
  );

  return {
    createRecord,
    updateRecord,
    deleteRecord,
    isUpdating,
    error,
    writeTargetResolution,
    resolveWriteTarget,
  };
}
